/**
 * Typography normalisation used to anchor quotes in Word paragraphs.
 *
 * Exact TypeScript port of `app/word_kit/normalize.py`; both must produce identical output for
 * `tests/word_kit/golden/normalize_vectors.json`. All offsets here are JavaScript `String` indices
 * (UTF-16 code units). Python reports code-point offsets for the same spans; the golden vectors pin both.
 *
 * Stages:
 * 1. `normalizeChars` maps single BMP code units to single BMP code units through `CHAR_MAP`
 *    (curly quotes/primes -> straight, every dash/minus -> "-", special spaces -> " "). Length-preserving.
 * 2. `collapseWhitespace` rewrites each maximal run of `WHITESPACE` as one " " and drops `IGNORABLE`
 *    characters, keeping for every output code unit the `[start, end)` input span it came from.
 * 3. `casefoldChars` lower-cases per code point, keeping any code point whose lower-case form is not
 *    exactly one code point (U+0130 stays; ß is never expanded).
 */

const SINGLE_QUOTES = ["‘", "’", "‚", "‛", "′", "ʼ", "＇"];
const DOUBLE_QUOTES = ["“", "”", "„", "‟", "″", "＂"];
const DASHES = [
  "\u001e", // Word's non-breaking hyphen as it appears in range text
  "‐",
  "‑",
  "‒",
  "–",
  "—",
  "―",
  "−",
  "﹘",
  "﹣",
  "－",
];
const SPACES = [
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  "　",
];

export const CHAR_MAP: ReadonlyMap<string, string> = new Map<string, string>([
  ...SINGLE_QUOTES.map((c): [string, string] => [c, "'"]),
  ...DOUBLE_QUOTES.map((c): [string, string] => [c, '"']),
  ...DASHES.map((c): [string, string] => [c, "-"]),
  ...SPACES.map((c): [string, string] => [c, " "]),
]);

/** Characters collapsed by `collapseWhitespace` (after `CHAR_MAP`, so NBSP etc. are included). */
export const WHITESPACE: ReadonlySet<string> = new Set([
  " ",
  "\t",
  "\n",
  "\r",
  "\u000b",
  "\u000c",
  "\u0085",
  " ",
  " ",
]);

/** Characters dropped by `collapseWhitespace`; a match never starts or ends on one. */
export const IGNORABLE: ReadonlySet<string> = new Set([
  "­", // SOFT HYPHEN
  "\u001f", // Word's optional hyphen as it appears in range text
  "\u0005", // Word's comment-anchor mark as it appears in paragraph text (Word on the web)
  "​",
  "‌",
  "‍",
  "⁠",
  "﻿",
]);

/**
 * Python's `str.isspace()` set, restricted to what can survive `normalizeForMatch` plus the rest for
 * completeness. Used only for the "blank quote" rule, which in Python is `not text.strip()`.
 */
const PY_WHITESPACE: ReadonlySet<string> = new Set([
  "\t",
  "\n",
  "\u000b",
  "\u000c",
  "\r",
  "\u001c",
  "\u001d",
  "\u001e",
  "\u001f",
  " ",
  "\u0085",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  " ",
  "　",
]);

/** Collapsed text plus, per output UTF-16 code unit, the `[start, end)` input span it came from. */
export interface Collapsed {
  readonly text: string;
  readonly starts: readonly number[];
  readonly ends: readonly number[];
}

/** Map a non-empty `[start, end)` range of `collapsed.text` back to the original string. */
export function sourceSpan(collapsed: Collapsed, start: number, end: number): [number, number] {
  if (!(start >= 0 && start < end && end <= collapsed.text.length)) {
    throw new RangeError(`invalid collapsed range [${start}, ${end}) for length ${collapsed.text.length}`);
  }
  return [collapsed.starts[start] as number, collapsed.ends[end - 1] as number];
}

/** Apply `CHAR_MAP`. The result has exactly the input's length. */
export function normalizeChars(text: string): string {
  let out = "";
  for (let i = 0; i < text.length; i++) {
    const ch = text[i] as string;
    out += CHAR_MAP.get(ch) ?? ch;
  }
  return out;
}

/** Collapse whitespace runs to one space and drop ignorable characters, keeping an offset map. */
export function collapseWhitespace(text: string): Collapsed {
  const out: string[] = [];
  const starts: number[] = [];
  const ends: number[] = [];
  let inRun = false;
  for (let index = 0; index < text.length; index++) {
    const ch = text[index] as string;
    if (IGNORABLE.has(ch)) continue;
    if (WHITESPACE.has(ch)) {
      if (inRun) {
        ends[ends.length - 1] = index + 1;
      } else {
        out.push(" ");
        starts.push(index);
        ends.push(index + 1);
        inRun = true;
      }
      continue;
    }
    inRun = false;
    out.push(ch);
    starts.push(index);
    ends.push(index + 1);
  }
  return { text: out.join(""), starts, ends };
}

/** `normalizeChars` then `collapseWhitespace`; spans index into `text`. */
export function normalizeForMatch(text: string): Collapsed {
  return collapseWhitespace(normalizeChars(text));
}

/**
 * Unicode version skew (red-team S11): `toLowerCase` uses the JS engine's Unicode tables (16.0 in current
 * engines), Python's `str.lower` its own (15.0 on CPython 3.12), so a few newly cased code points (e.g. U+A7CB)
 * fold here but not on the server. That can only make the two sides DISAGREE ABOUT A MATCH, never pick a
 * different place: new Unicode versions only add case mappings, so the side with newer tables sees a superset
 * of the other's case-insensitive hits. Where the server found exactly one hit, the pane finds that same hit
 * (ok) or more than one (ambiguous -> refused); where the server found none it never published the edit. The
 * anchor ladder is unique-or-refuse on both sides, so skew costs a refusal, not a misplaced edit. Aligning the
 * tables would mean shipping a frozen case-mapping table here and in `normalize.py`; not worth it for a handful
 * of rare letters. The golden vectors stay on characters both versions agree on.
 */
function lowerChar(ch: string): string {
  const lowered = ch.toLowerCase();
  // One code point, and the same UTF-16 width (always true in practice; guarded so offsets can never drift).
  return [...lowered].length === 1 && lowered.length === ch.length ? lowered : ch;
}

/** Lower-case per code point, keeping any code point whose lower form is not one code point. */
export function casefoldChars(text: string): string {
  let out = "";
  for (const ch of text) out += lowerChar(ch);
  return out;
}

/** Python's `not text.strip()`. */
export function isPyBlank(text: string): boolean {
  for (const ch of text) if (!PY_WHITESPACE.has(ch)) return false;
  return true;
}

/** Number of Unicode code points (what Python's `len()` reports). */
export function codePointLength(text: string): number {
  let n = 0;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    if (c >= 0xd800 && c <= 0xdbff && i + 1 < text.length) {
      const d = text.charCodeAt(i + 1);
      if (d >= 0xdc00 && d <= 0xdfff) i++;
    }
    n++;
  }
  return n;
}

/** Replace every lone UTF-16 surrogate with U+FFFD (the server rejects lone surrogates). */
export function replaceLoneSurrogates(text: string): string {
  // Fast path: most text has no surrogates at all.
  if (!/[\ud800-\udfff]/.test(text)) return text;
  let out = "";
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    if (c >= 0xd800 && c <= 0xdbff) {
      const d = i + 1 < text.length ? text.charCodeAt(i + 1) : 0;
      if (d >= 0xdc00 && d <= 0xdfff) {
        out += text[i]! + text[i + 1]!;
        i++;
      } else {
        out += "�";
      }
    } else if (c >= 0xdc00 && c <= 0xdfff) {
      out += "�";
    } else {
      out += text[i]!;
    }
  }
  return out;
}
